import React from 'react';
import { ContextMenuState, OverlayMenuEnum, OverlayMenuState } from 'src/types/ViewModel/Vessel/TankPlan';
import { useStowageStore } from 'src/context/StowageContext';
import { canLevelTanks, levelTanks } from 'src/utils/stowage/stowageOperation/levelTanksUtils';
import { useRotationStore } from 'src/context/RotationContext';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { useAuthStore } from 'src/context/AuthContext';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import { isLoadingOrUnloadingCargoOperation } from 'src/utils/stowage/cargoOperationUtils';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { isTankBlocked } from 'src/utils/stowage/stowageOperation/blockedTankUtils';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { createDeleteStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import OdfjellContextMenu, { MenuItem } from 'src/components/ui/odfjellContextMenu/OdfjellContextMenu';
import { useVesselStore } from 'src/context/VesselContext';
import { undoStowageForCargoAndTanks } from 'src/utils/stowage/stowageOperation/undoStowageUtils';
import { deepCopyStowagePlan } from 'src/factory/Stowage/StowagePlanFactory';
import { useViolationsStore } from 'src/context/ViolationsContext';

interface TankContextMenuProps {
    contextMenuState: ContextMenuState;
}
const TankContextMenu: React.FC<TankContextMenuProps> = ({ contextMenuState }) => {
    const { selectedStowageRotationPortCall, selectedStowageRotationBerthCall, stowageRotationTree } = useRotationStore();
    const {
        setOverlayMenuState,
        setTankContextMenuState,
        setIsShipFigurePanelOpen,
        isShipFigurePanelOpen,
        setIsFillPanelOpen,
        isFillPanelOpen,
        activeStowagePlan,
        setIsBlockTankPanelOpen,
        isBlockTankPanelOpen,
    } = useStowageStore();
    const { violations } = useViolationsStore();
    const { selectedVessel } = useVesselStore();

    const { selectedCargoOperation, setSelectedCargoOperation, selectedTanks, setSelectedTanks, setSelectedTab, setSelectedViolationListFilter } =
        useStowageSelectionStore();
    const { role } = useAuthStore();
    const { saveStowageData } = usePostStowageApi();

    const getBerthCallForOperation = (): RotationBerthCall => {
        let _selectedStowageRotationBerthCall = selectedStowageRotationBerthCall;
        if (!_selectedStowageRotationBerthCall && selectedCargoOperation) {
            _selectedStowageRotationBerthCall = selectedCargoOperation.berthCall;
        }
        return _selectedStowageRotationBerthCall;
    };

    const isOpenTankDetailsEnabled = () => {
        return true;
    };

    const onOpenTankDetails = () => {
        setOverlayMenuState({ visible: true, type: OverlayMenuEnum.TankDetails, tank: contextMenuState.tank } as OverlayMenuState);
    };

    const isLevelTanksEnabled = () => {
        return (
            hasPermission(role, stowageFeatureEnum.EditStowage) &&
            canLevelTanks(activeStowagePlan, selectedStowageRotationPortCall, selectedStowageRotationBerthCall, selectedTanks)
        );
    };
    const onLevelTanks = async () => {
        if (isLevelTanksEnabled()) {
            const _stowagePlan = deepCopyStowagePlan(activeStowagePlan);
            const stowagePlanChanges = levelTanks(
                _stowagePlan,
                selectedStowageRotationPortCall,
                getBerthCallForOperation(),
                selectedTanks,
                selectedVessel
            );
            await saveStowageData(_stowagePlan, stowagePlanChanges);
        }
    };

    const onUndoStowage = async () => {
        const cargo = contextMenuState.cargoContents[0]?.cargo;
        if (!activeStowagePlan || !cargo) {
            return;
        }
        const _stowagePlan = deepCopyStowagePlan(activeStowagePlan);
        
        // Use selected tanks if available, otherwise fall back to context menu tank
        const tanksToUndo = selectedTanks && selectedTanks.length > 0 ? selectedTanks : [contextMenuState.tank];
        
        const stowagePlanChanges = undoStowageForCargoAndTanks(_stowagePlan, cargo, tanksToUndo);
        await saveStowageData(activeStowagePlan, stowagePlanChanges);
    };

    const getEditedStowageItem = () => {
        return contextMenuState.cargoContents?.[0]?.stowage;
    };

    const isEditStowageEnabled = () => {
        const stowageItem = getEditedStowageItem();
        return (
            hasPermission(role, stowageFeatureEnum.EditStowage) &&
            stowageItem &&
            isLoadingOrUnloadingCargoOperation(stowageItem.cargoOperation) &&
            stowageItem.cargoOperation?.berthCall?.portCall?.portCallGuid === selectedStowageRotationPortCall?.portCallGuid
        );
    };

    const onEditStowage = () => {
        if (isEditStowageEnabled()) {
            setOverlayMenuState({
                visible: true,
                type: OverlayMenuEnum.EditStowage,
                tank: contextMenuState.tank,
                cargoContents: contextMenuState.cargoContents,
            } as OverlayMenuState);

            setSelectedCargoOperation(getEditedStowageItem().cargoOperation);
            setIsFillPanelOpen(!isFillPanelOpen);
            setSelectedTanks([contextMenuState.tank]);
        }
    };

    const isEditShipFigureEnabled = () => {
        return hasPermission(role, stowageFeatureEnum.EditShipFigure) && contextMenuState.cargoContents?.length > 0;
    };

    const onEditShipFigure = () => {
        if (isEditShipFigureEnabled()) {
            setOverlayMenuState({ visible: true, type: OverlayMenuEnum.EditShipFigure, tank: contextMenuState.tank } as OverlayMenuState);
            setSelectedCargoOperation(contextMenuState.cargoContents[0].cargoOperation);
            setIsShipFigurePanelOpen(!isShipFigurePanelOpen);
            setSelectedTanks([contextMenuState.tank]);
        }
    };

    const isBlockTankEnabled = () => {
        return (
            hasPermission(role, stowageFeatureEnum.BlockTank) &&
            contextMenuState.cargoContents?.length === 0 &&
            selectedStowageRotationPortCall !== null
        );
    };

    const onBlockTank = () => {
        if (isBlockTankEnabled()) {
            setOverlayMenuState({ visible: true, type: OverlayMenuEnum.BlockTank, tank: contextMenuState.tank } as OverlayMenuState);
            setIsBlockTankPanelOpen(!isBlockTankPanelOpen);
        }
    };

    const onUnBlockTank = async () => {
        const tank = contextMenuState.tank;
        const blockedTank = activeStowagePlan?.blockedTanks?.find((t) => t.tank.tankId === tank.tankId);

        if (blockedTank) {
            const stowagePlanChanges: StowagePlanChange[] = [
                createDeleteStowagePlanChange(activeStowagePlan.stowagePlanGuid, blockedTank.blockedTankGuid, StowageChangeDataType.BlockedTank),
            ];
            await saveStowageData(activeStowagePlan, stowagePlanChanges);
        }
    };

    const isViewViolationsEnabled = () => {
        const tank = contextMenuState.tank;
        if (!tank || !violations) {
            return false;
        }

        // Check if the tank has violations in the current context
        const hasTankViolations = violations.some((violation) => {
            // Check if violation is for the current tank
            const isTankMatch = violation.tank?.tankId === tank.tankId;

            // Check if violation matches current port call or berth call context
            // If a berth call is selected, check for violations at that specific berth call
            // Otherwise fall back to checking for violations at the port call level
            const isContextMatch = selectedStowageRotationBerthCall
                ? violation.berthCall?.berthCallGuid === selectedStowageRotationBerthCall.berthCallGuid
                : selectedStowageRotationPortCall && violation.portCall?.portCallGuid === selectedStowageRotationPortCall.portCallGuid;

            // Return true if it's a violation for this tank in the current context
            return isTankMatch && isContextMatch;
        });

        return hasTankViolations;
    };

    const onViewViolations = () => {
        if (isViewViolationsEnabled()) {
            setSelectedTab('Violation List');
            setSelectedTanks([contextMenuState.tank]);
            setSelectedViolationListFilter('Selected tank');
        }
    };

    const getBlockTankMenuItem = () => {
        const tank = contextMenuState.tank;

        const _isBlocked = isTankBlocked(
            activeStowagePlan,
            tank,
            selectedStowageRotationPortCall,
            selectedStowageRotationBerthCall,
            stowageRotationTree
        );

        if (_isBlocked) {
            return { content: 'Unblock tank', permission: stowageFeatureEnum.BlockTank, isEnabled: () => true, action: onUnBlockTank };
        } else {
            return { content: 'Block tank', permission: stowageFeatureEnum.BlockTank, isEnabled: isBlockTankEnabled, action: onBlockTank };
        }
    };

    const isViewPreviousCargoesEnabled = () => {
        return true;
    };

    const onViewPreviousCargoes = () => {
        setOverlayMenuState({ visible: true, type: OverlayMenuEnum.PreviousCargoes, tank: contextMenuState.tank } as OverlayMenuState);
    };

    const closeContextMenu = () => {
        setTankContextMenuState({ visible: false, cargoContents: [] } as ContextMenuState);
    };

    const handleOperation = async (event: React.MouseEvent, operation: (() => Promise<void>) | (() => void)) => {
        event.stopPropagation();

        if (!activeStowagePlan) {
            closeContextMenu();
            return;
        }
        const result = operation();
        if (result instanceof Promise) {
            await result;
        }
        closeContextMenu();
    };

    const menuItemConfigs = [
        {
            content: 'View tank details',
            permission: stowageFeatureEnum.ViewStowagePlan,
            isEnabled: isOpenTankDetailsEnabled,
            action: onOpenTankDetails,
            className: null,
        },
        {
            content: 'View previous cargoes',
            permission: stowageFeatureEnum.ViewStowagePlan,
            isEnabled: isViewPreviousCargoesEnabled,
            action: onViewPreviousCargoes,
        },
        {
            content: 'View violations',
            permission: stowageFeatureEnum.ViewStowagePlan,
            isEnabled: isViewViolationsEnabled,
            action: onViewViolations,
        },
        { isSeparator: true },
        getBlockTankMenuItem(),
        { content: 'Edit stowage', permission: stowageFeatureEnum.EditStowage, isEnabled: isEditStowageEnabled, action: onEditStowage },
        {
            content: 'Edit ship figure',
            permission: stowageFeatureEnum.EditShipFigure,
            isEnabled: isEditShipFigureEnabled,
            action: onEditShipFigure,
        },
        { content: 'Level tanks', permission: stowageFeatureEnum.EditStowage, isEnabled: isLevelTanksEnabled, action: onLevelTanks },
        { content: 'Undo stowage', permission: stowageFeatureEnum.EditStowage, isEnabled: isEditStowageEnabled, action: onUndoStowage },
    ];

    const createMenuItems = (): MenuItem[] =>
        menuItemConfigs.map((config) => ({
            isSeparator: config.isSeparator ?? false,
            content: config.content,
            isDisabled: !(hasPermission(role, config.permission) && config.isEnabled?.()),
            onClick: config.action ? async (event) => handleOperation(event, config.action) : undefined,
            className: config.className,
        }));

    return (
        <OdfjellContextMenu
            position={{ x: contextMenuState.x, y: contextMenuState.y }}
            menuItems={createMenuItems()}
            dataContextMenu="tank"
            className="tank-context-menu"
        />
    );
};

export default TankContextMenu;
